import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Edit3, Save, Loader, Clock, Users, CheckCircle, CheckCircle2, XCircle, ChevronDown, Lock, Trash2, Briefcase, DollarSign, Send, Trophy, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AWARD_STATUSES, LOSS_REASONS } from '../../lib/constants';
import { ACTIVITY_TYPES, getActivityMeta, formatActivityMessage } from '../../utils/helpers';
import { tendersService, servicesService, sourcesService, API_BASE_URL, approvalService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUsers } from '../../hooks';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker, Toast } from '../ui';
import DeleteConfirmModal from './DeleteConfirmModal';
import ApproveRequestModal from './ApproveRequestModal';
import RejectRequestModal from './RejectRequestModal';
import TenderWonConfirmModal from './TenderWonConfirmModal';

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const baseUrl = API_BASE_URL.replace(/\/api$/, '');
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

export default function TenderDetailModal({
  tender,
  currentUser,
  onClose,
  onSave,
  onDelete,
  onRefreshTenders
}) {
  const { t } = useLanguage();
  const { users } = useUsers();
  const [activeTab, setActiveTab] = useState('details');
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState(() => ({
    ...tender,
    services: (tender.services || []).map(s => typeof s === 'object' && s !== null ? s.name : s).filter(Boolean),
  }));
  const [referenceData, setReferenceData] = useState(() => tender);
  const [availableServices, setAvailableServices] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    servicesService.getAll().then(res => {
      if (isMounted) setAvailableServices(Array.isArray(res) ? res : []);
    }).catch(e => console.error(e));
    sourcesService.getAll().then(res => {
      if (isMounted) setAvailableSources(Array.isArray(res) ? res : []);
    }).catch(e => console.error(e));
    return () => { isMounted = false; };
  }, []);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stageChangeConfirm, setStageChangeConfirm] = useState(null);
  const [stageChangeNote, setStageChangeNote] = useState('');
  const [lossReason, setLossReason] = useState('');
  const [isStageChanging, setIsStageChanging] = useState(false);

  // ── Approval Workflow State ───────────────────────────────────────────────
  const [approvalRequest, setApprovalRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const isApprover = ['CEO', 'GM_Operations', 'BM_Operations'].includes(currentUser?.role);
  const approvalStatus = formData.approvalStatus;

  const [timelineActivities, setTimelineActivities] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const activityEndRef = useRef(null);

  const fetchTimeline = async () => {
    setTimelineLoading(true);
    try {
      const activities = await tendersService.getTimeline(tender.id);
      setTimelineActivities(activities);
    } catch (error) {
      console.error('Error fetching tender timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  };

  useEffect(() => {
    if (tender.id) fetchTimeline();
  }, [tender.id]);

  // ── Fetch ApprovalRequest for this tender ─────────────────────────────────
  useEffect(() => {
    if (!tender?.id) return;
    approvalService.getByEntity('TENDER', tender.id)
      .then(req => { if (req) setApprovalRequest(req); })
      .catch(err => console.error('[Approval] fetch error:', err));
  }, [tender?.id]);

  const handleApproveInModal = async ({ assignedSalespersonId, remarks }) => {
    try {
      let targetRequestId = approvalRequest?.id;
      if (!targetRequestId) {
        const fresh = await approvalService.getByEntity('TENDER', tender.id);
        if (fresh?.id) targetRequestId = fresh.id;
      }
      if (!targetRequestId) {
        throw new Error('Approval request ID not found. Please refresh and try again.');
      }
      const res = await approvalService.approve(targetRequestId, { assignedSalespersonId, remarks });
      setApprovalRequest(res.data);
      setFormData(prev => ({
        ...prev,
        approvalStatus: 'APPROVED',
        approvalRemarks: remarks || 'Approved by management'
      }));
      setShowApproveModal(false);
      setToast({ type: 'success', message: `${formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender'} approved successfully!` });
      if (onSave) onSave({ ...tender, approvalStatus: 'APPROVED', approvalRemarks: remarks });
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to approve' });
      throw err;
    }
  };

  const handleRejectInModal = async ({ remarks }) => {
    try {
      let targetRequestId = approvalRequest?.id;
      if (!targetRequestId) {
        const fresh = await approvalService.getByEntity('TENDER', tender.id);
        if (fresh?.id) targetRequestId = fresh.id;
      }
      if (!targetRequestId) {
        throw new Error('Approval request ID not found. Please refresh and try again.');
      }
      const res = await approvalService.reject(targetRequestId, { remarks });
      setApprovalRequest(res.data);
      setFormData(prev => ({
        ...prev,
        approvalStatus: 'REJECTED',
        approvalRemarks: remarks
      }));
      setShowRejectModal(false);
      setToast({ type: 'error', message: `${formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender'} rejected.` });
      if (onSave) onSave({ ...tender, approvalStatus: 'REJECTED', approvalRemarks: remarks });
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to reject' });
      throw err;
    }
  };

  const timeline = useMemo(() => {
    return timelineActivities.map((a) => {
      let content = {};
      let raw = a.content;
      let isProfilePhase = false;
      if (typeof raw === 'string' && raw.startsWith('[Profile Phase] ')) {
        isProfilePhase = true;
        raw = raw.substring('[Profile Phase] '.length);
      }
      try {
        content = JSON.parse(raw);
      } catch (e) {
        content = { note: raw };
      }
      return {
        type: a.type?.toLowerCase() || 'note_added',
        data: content,
        timestamp: new Date(a.createdAt),
        user: a.user?.username || 'System',
        isPrequalHistory: !!a.isPrequalificationHistory,
        isProfileHistory: !!a.isProfileHistory || isProfilePhase
      };
    }).sort((a, b) => a.timestamp - b.timestamp);
  }, [timelineActivities]);

  useEffect(() => {
    if (activeTab === 'activity') activityEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeTab, timeline]);

  // Auto-calc GP%
  useEffect(() => {
    const pv = Number(formData.proposalValue) || 0;
    const gp = Number(formData.grossProfit) || 0;
    if (pv > 0 && gp >= 0) {
      setFormData(prev => ({ ...prev, gpPercent: ((gp / pv) * 100).toFixed(2) }));
    } else {
      setFormData(prev => ({ ...prev, gpPercent: '' }));
    }
  }, [formData.proposalValue, formData.grossProfit]);

  const isLockedStatus = ['Completed'].includes(formData.status);
  const isCeo = currentUser?.role === 'CEO';

  const handleStageClick = (newStage) => {
    if (newStage === formData.status) return;
    if (isLockedStatus) return;
    setStageChangeConfirm({ from: formData.status, to: newStage });
  };

  const prepareDataForSave = (data) => ({
    ...data,
    services: data.services || [],
    assignedTo: data.assignedTo ? parseInt(data.assignedTo) : null,
    proposalValue: data.proposalValue ? Number(data.proposalValue) : null,
    negotiation: data.negotiation ? Number(data.negotiation) : null,
    grossProfit: data.grossProfit ? Number(data.grossProfit) : null,
    gpPercent: data.gpPercent ? Number(data.gpPercent) : null,
    wht: data.wht ? Number(data.wht) : null,
    mobilizationAdvance: data.mobilizationAdvance ? Number(data.mobilizationAdvance) : null,
    noOfStaff: data.noOfStaff ? Number(data.noOfStaff) : null,
    earnestMoneyValue: data.earnestMoneyValue ? Number(data.earnestMoneyValue) : null,
  });

  const handleStageChangeConfirm = async () => {
    if (!stageChangeConfirm) return;
    setIsStageChanging(true);
    const { from, to } = stageChangeConfirm;

    try {
      const isLoss = to === 'Lost';
      const lossLabel = isLoss ? (LOSS_REASONS.find((r) => r.id === lossReason)?.label || lossReason) : null;

      const changes = getChangedFieldsData();
      const savedData = {
        ...formData,
        status: to,
        ...(isLoss ? { lostReason: lossReason, lostReasonNote: stageChangeNote || null } : {}),
      };

      await onSave(tender.id, prepareDataForSave(savedData));

      setFormData(prev => ({ ...prev, status: to }));
      setReferenceData(savedData);
      setStageChangeConfirm(null);
      setStageChangeNote('');
      setLossReason('');

      await tendersService.addNote(tender.id, {
        content: JSON.stringify({ 
          from, 
          to, 
          note: stageChangeNote,
          lossReason: lossLabel,
          editedFields: changes.length > 0 ? changes.map(c => c.label).join(', ') : undefined,
          changes: changes.length > 0 ? changes : undefined
        }),
        type: 'STAGE_CHANGE',
        userId: currentUser?.id
      });
      fetchTimeline();
    } catch (error) {
      console.error("Error changing tender stage:", error);
      setToast({ message: 'Error changing stage', type: 'error' });
    } finally {
      setIsStageChanging(false);
    }
  };

  const handleStageChangeCancel = () => {
    setStageChangeConfirm(null);
    setStageChangeNote('');
    setLossReason('');
  };

  const getChangedFieldsData = () => {
    const changes = [];
    const fieldsToCheck = [
      { key: 'title', label: 'Title' },
      { key: 'type', label: 'Type' },
      { key: 'assignedTo', label: 'Assignee' },
      { key: 'proposalValue', label: 'Proposal Value' },
      { key: 'negotiation', label: 'Negotiated Amount' },
      { key: 'grossProfit', label: 'Gross Profit' },
      { key: 'wht', label: 'WHT' },
      { key: 'mobilizationAdvance', label: 'Mobilization Advance' },
      { key: 'contractDuration', label: 'Contract Duration' },
      { key: 'noOfStaff', label: 'No of Staff' },
      { key: 'earnestMoneyValue', label: 'Earnest Money Value' },
      { key: 'paymentTerms', label: 'Payment Terms' },
      { key: 'winningStrategy', label: 'Winning Strategy' },
      { key: 'notes', label: 'Notes' }
    ];

    const oldServices = referenceData.services?.map(s => typeof s === 'object' ? s.name : s).sort().join(', ') || '';
    const newServices = formData.services?.sort().join(', ') || '';
    if (oldServices !== newServices) {
      changes.push({ label: 'Services', old: oldServices, new: newServices });
    }

    fieldsToCheck.forEach(field => {
      let originalValue = referenceData[field.key];
      let newValue = formData[field.key];
      
      let strOriginal = originalValue === null || originalValue === undefined ? '' : String(originalValue).trim();
      let strNew = newValue === null || newValue === undefined ? '' : String(newValue).trim();

      if (strOriginal !== strNew) {
        changes.push({ label: field.label, old: strOriginal, new: strNew });
      }
    });

    return changes;
  };

  const handleSave = async () => {
    if (isLockedStatus) return;
    setIsSaving(true);
    
    const changes = getChangedFieldsData();
    if (changes.length > 0) {
      try {
        await tendersService.addNote(tender.id, {
          content: JSON.stringify({ 
            field: changes.map(c => c.label).join(', '),
            changes: changes
          }),
          type: 'details_updated',
          userId: currentUser?.id
        });
        fetchTimeline();
      } catch (error) {
        console.error('Failed to log detail update:', error);
        setToast({ message: 'Failed to log timeline update', type: 'error' });
      }
    }

    try {
      await onSave(tender.id, prepareDataForSave(formData));
      setReferenceData(formData);
      setToast({ message: 'Tender updated successfully', type: 'success' });
    } catch (error) {
      console.error('Error saving tender:', error);
      setToast({ message: 'Failed to save tender details', type: 'error' });
    } finally {
      setIsSaving(false);
      // We do not close the modal here so the user can see the updated timeline
    }
  };

  const [comment, setComment] = useState('');
  const handleAddComment = async () => {
    if (!comment.trim()) return;
    try {
      await tendersService.addNote(tender.id, {
        content: JSON.stringify({ note: comment }),
        type: 'NOTE',
        userId: currentUser?.id
      });
      setComment('');
      fetchTimeline();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const profileObj = tender.profile || {};

  const renderFormFields = () => (
    <>
    {/* Profile Card (read-only) */}
                    <div className="bg-indigo-50/30 p-4 rounded-xl border border-indigo-200 shadow-sm">
                      <h3 className="text-xs font-bold text-indigo-700 uppercase tracking-widest flex items-center gap-2 mb-3">
                        <Users className="w-3.5 h-3.5" /> Client Profile
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase">Client Name</label>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{profileObj.clientName || '—'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase">Contact Person</label>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{profileObj.contactPersonName || '—'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase">Mobile</label>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{profileObj.mobile || '—'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase">Email</label>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{profileObj.email || '—'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase">City</label>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{profileObj.city || '—'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-400 uppercase">Project</label>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{profileObj.projectName || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">{t('source') || 'Source'}</p>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">{profileObj.source?.name || profileObj.source || tender.source?.name || '-'}</p>
                        </div>
                      </div>
                    </div>
    

                    {/* Previous Tenders History */}
                    {tender.profile?.tenders && tender.profile.tenders.filter(t => String(t.id) !== String(tender.id) && new Date(t.createdAt) < new Date(tender.createdAt)).length > 0 && (
                      <div className="bg-purple-50/30 p-4 rounded-xl border border-purple-200 shadow-sm mb-4">
                        <h3 className="text-xs font-bold text-purple-700 uppercase tracking-widest flex items-center gap-2 mb-3">
                          <Clock className="w-3.5 h-3.5" /> Previous Tenders / Proposals
                        </h3>
                        <div className="space-y-4">
                          {tender.profile.tenders.filter(t => String(t.id) !== String(tender.id) && new Date(t.createdAt) < new Date(tender.createdAt)).map(prevTender => (
                            <div key={prevTender.id} className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-3 border-b border-purple-100 last:border-0 pb-3 last:pb-0">
                              <div className="col-span-1 sm:col-span-3">
                                <label className="block text-xs font-bold text-gray-400 uppercase">Tender / Proposal Title</label>
                                <p className="text-sm font-bold text-purple-900 mt-0.5">
                                  {prevTender.title || 'Untitled'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase">Created On</label>
                                <p className="text-sm font-medium text-gray-900 mt-0.5">
                                  {prevTender.createdAt ? new Date(prevTender.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase">Submission Date</label>
                                <p className="text-sm font-medium text-gray-900 mt-0.5">
                                  {prevTender.submissionDate ? new Date(prevTender.submissionDate).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase">Type</label>
                                <p className="text-sm font-medium text-gray-900 mt-0.5">
                                  {prevTender.type === 'PROPOSAL' ? 'Proposal' : 'Tender'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase">Source</label>
                                <p className="text-sm font-medium text-gray-900 mt-0.5">
                                  {prevTender.source?.name || '—'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase">Assignee</label>
                                <p className="text-sm font-medium text-gray-900 mt-0.5">
                                  {prevTender.assignee?.username || prevTender.assignee?.name || '—'}
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase">Status</label>
                                <p className="text-sm font-medium text-gray-900 mt-0.5">
                                  {prevTender.status || '—'}
                                </p>
                              </div>
                              <div className="sm:col-span-3">
                                <label className="block text-xs font-bold text-gray-400 uppercase">Services</label>
                                <p className="text-sm font-medium text-gray-900 mt-0.5">
                                  {prevTender.services?.length ? prevTender.services.map(s => typeof s === 'string' ? s : s.name).join(', ') : '—'}
                                </p>
                              </div>
                              {prevTender.status === 'Lost' && (
                                <div className="sm:col-span-3 bg-red-50/50 p-2.5 rounded-lg border border-red-100">
                                  <label className="block text-[10px] font-bold text-red-500 uppercase tracking-wide">Lost Reason</label>
                                  <p className="text-sm font-semibold text-red-700 mt-0.5">
                                    {prevTender.lostReason || 'Not specified'} 
                                    {prevTender.lostReasonNote ? <span className="font-normal text-red-600 ml-1">- {prevTender.lostReasonNote}</span> : ''}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Prequalification History Details */}
                    {tender.prequalification && (
                      <div className="bg-teal-50/30 p-4 rounded-xl border border-teal-200 shadow-sm">
                        <h3 className="text-xs font-bold text-teal-700 uppercase tracking-widest flex items-center gap-2 mb-3">
                          <Clock className="w-3.5 h-3.5" /> Prequalification History
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-3">
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase">Created On</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {tender.prequalification.createdAt ? new Date(tender.prequalification.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase">Submission Date</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {tender.prequalification.submissionDate ? new Date(tender.prequalification.submissionDate).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase">Enlisted On</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {tender.prequalification.enlistedDate 
                                ? new Date(tender.prequalification.enlistedDate).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) 
                                : (tender.prequalification.updatedAt ? new Date(tender.prequalification.updatedAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—')}
                            </p>
                          </div>
                          
                          {/* Additional Details */}
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase">Assignee</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {tender.prequalification.assignee?.username || tender.prequalification.assignee?.name || '—'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase">Source</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {tender.prequalification.source?.name || '—'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase">Document Status</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {tender.prequalification.documentStatus || '—'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase">Earnest Money</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {tender.prequalification.earnestMoneyRequired 
                                ? (tender.prequalification.earnestMoneyValue ? `Required (${tender.prequalification.earnestMoneyValue})` : 'Required') 
                                : 'Not Required'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-400 uppercase">Attachment URL</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5 truncate">
                              {tender.prequalification.attachmentUrl ? (
                                <a href={getFileUrl(tender.prequalification.attachmentUrl)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                  View Document
                                </a>
                              ) : '—'}
                            </p>
                          </div>
                          <div className="sm:col-span-3">
                            <label className="block text-xs font-bold text-gray-400 uppercase">Services</label>
                            <p className="text-sm font-medium text-gray-900 mt-0.5">
                              {tender.prequalification.services?.length ? tender.prequalification.services.map(s => typeof s === 'string' ? s : s.name).join(', ') : '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
    
                    {/* General Details */}
                    <div className="bg-blue-50/30 p-6 rounded-xl border border-blue-200 shadow-sm space-y-4">
                      <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-blue-200 pb-3">
                        <Edit3 className="w-4 h-4 shrink-0" /> General Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Title</label>
                          <input type="text" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                            value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Type</label>
                          <Dropdown className="w-full mt-1">
                            <DropdownTrigger className="w-full">
                              <div className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500">
                                <span>{formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender'}</span>
                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                              </div>
                            </DropdownTrigger>
                            <DropdownPopover placement="bottom">
                              <DropdownMenu
                                selectedKeys={new Set([formData.type || 'TENDER'])}
                                onSelectionChange={(keys) => setFormData({ ...formData, type: Array.from(keys)[0] })}
                                selectionMode="single"
                              >
                                <DropdownItem id="TENDER">Tender</DropdownItem>
                                <DropdownItem id="PROPOSAL">Proposal</DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Assignee</label>
                          <Dropdown className="w-full mt-1">
                            <DropdownTrigger className="w-full">
                              <div className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500">
                                <span className="truncate">
                                  {formData.assignedTo
                                    ? (users?.find(u => u.id === formData.assignedTo)?.username || users?.find(u => u.id === formData.assignedTo)?.name || formData.assignedTo)
                                    : 'Select Assignee'}
                                </span>
                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                              </div>
                            </DropdownTrigger>
                            <DropdownPopover placement="bottom">
                              <DropdownMenu
                                selectedKeys={new Set([formData.assignedTo])}
                                onSelectionChange={(keys) => setFormData({ ...formData, assignedTo: Array.from(keys)[0] })}
                                selectionMode="single"
                              >
                                {users?.map(u => (
                                  <DropdownItem key={u.id} id={u.id}>{u.username || u.name}</DropdownItem>
                                ))}
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Source</label>
                          <Dropdown className="w-full mt-1">
                            <DropdownTrigger className="w-full">
                              <div className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500">
                                <span className="truncate">
                                  {formData.sourceId
                                    ? (availableSources?.find(s => s.id === formData.sourceId)?.name || formData.sourceId)
                                    : 'Select Source'}
                                </span>
                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                              </div>
                            </DropdownTrigger>
                            <DropdownPopover placement="bottom">
                              <DropdownMenu
                                selectedKeys={new Set([formData.sourceId])}
                                onSelectionChange={(keys) => setFormData({ ...formData, sourceId: Number(Array.from(keys)[0]) })}
                                selectionMode="single"
                              >
                                {availableSources?.map(s => (
                                  <DropdownItem key={s.id} id={s.id}>{s.name}</DropdownItem>
                                ))}
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Submission Date</label>
                          <div className="mt-1">
                            <DatePicker
                              value={formData.submissionDate || ''}
                              onChange={(e) => setFormData({ ...formData, submissionDate: e.target.value })}
                              name="submissionDate"
                              placeholder="Select submission date"
                            />
                          </div>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="text-xs font-semibold text-gray-600">Services</label>
                          <Dropdown className="w-full mt-1">
                            <DropdownTrigger className="w-full">
                              <button
                                type="button"
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white min-h-[38px] flex items-center justify-between outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                              >
                                <div className="flex-1 truncate text-left">
                                  {formData.services?.length === 0 || !formData.services ? (
                                    <span className="text-gray-400">Select Services</span>
                                  ) : formData.services?.length === 1 ? (
                                    <span className="text-gray-900">{formData.services[0]}</span>
                                  ) : (
                                    <span className="text-blue-600 font-medium">
                                      {formData.services.length} Selected
                                    </span>
                                  )}
                                </div>
                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover placement="bottom" className="w-full max-h-60 overflow-y-auto">
                              <DropdownMenu
                                selectedKeys={new Set(formData.services || [])}
                                selectionMode="multiple"
                                onSelectionChange={(keys) => setFormData({ ...formData, services: Array.from(keys) })}
                              >
                                {(availableServices || []).map((s) => (
                                  <DropdownItem key={s.name} id={s.name}>{s.name}</DropdownItem>
                                ))}
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                      </div>
                    </div>
    
                    {/* Commercial Details */}
                    <div className="bg-amber-50/30 p-6 rounded-xl border border-amber-200 shadow-sm space-y-4">
                      <h3 className="text-xs font-bold text-amber-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-amber-200 pb-3">
                        <DollarSign className="w-4 h-4 shrink-0" /> Commercial Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Proposal Value</label>
                          <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 font-semibold text-blue-900 bg-white"
                            value={formData.proposalValue || ''} onChange={(e) => setFormData({ ...formData, proposalValue: e.target.value })} placeholder="e.g. 500000" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Negotiated Amount</label>
                          <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
                            value={formData.negotiation || ''} onChange={(e) => setFormData({ ...formData, negotiation: e.target.value })} placeholder="Negotiation amount" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Gross Profit</label>
                          <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
                            value={formData.grossProfit || ''} onChange={(e) => setFormData({ ...formData, grossProfit: e.target.value })} placeholder="e.g. 50000" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">GP %</label>
                          <input readOnly className="w-full p-2 border rounded-lg text-sm mt-1 bg-emerald-50 border-emerald-300 font-bold text-emerald-800 outline-none cursor-not-allowed"
                            value={formData.gpPercent || ''} placeholder="Auto calculated %" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">WHT</label>
                          <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
                            value={formData.wht || ''} onChange={(e) => setFormData({ ...formData, wht: e.target.value })} placeholder="WHT Amount or %" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Mobilization Advance</label>
                          <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
                            value={formData.mobilizationAdvance || ''} onChange={(e) => setFormData({ ...formData, mobilizationAdvance: e.target.value })} placeholder="Advance amount" />
                        </div>
                        <div className="relative">
                          <label className="text-xs font-semibold text-gray-600">Contract End Date / Duration</label>
                          <div className="mt-1">
                            <DatePicker
                              value={formData.contractDuration || ''}
                              onChange={(e) => setFormData({ ...formData, contractDuration: e.target.value })}
                              name="contractDuration"
                              placeholder="Select expiry date"
                            />
                          </div>
                          {(() => {
                            if (!formData.contractDuration || !formData.contractDuration.includes('-')) return null;
                            const target = new Date(formData.contractDuration);
                            if (isNaN(target.getTime())) return null;
                            const now = new Date();
                            if (target.getTime() <= now.getTime()) return <p className="text-[10px] text-red-500 font-medium mt-1">Expired</p>;
                            
                            let years = target.getFullYear() - now.getFullYear();
                            let months = target.getMonth() - now.getMonth();
                            let days = target.getDate() - now.getDate();
                            
                            if (days < 0) {
                              months -= 1;
                              days += new Date(target.getFullYear(), target.getMonth(), 0).getDate();
                            }
                            if (months < 0) {
                              years -= 1;
                              months += 12;
                            }
                            
                            const parts = [];
                            if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
                            if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
                            if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
                            
                            return (
                              <p className="text-[10px] text-blue-600 font-medium mt-1">
                                {parts.length > 0 ? parts.join(', ') + ' remaining' : 'Expires today'}
                              </p>
                            );
                          })()}
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">No of Staff</label>
                          <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
                            value={formData.noOfStaff || ''} onChange={(e) => setFormData({ ...formData, noOfStaff: e.target.value })} placeholder="Required staff count" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Earnest Money Value</label>
                          <input type="number" className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
                            value={formData.earnestMoneyValue || ''} onChange={(e) => setFormData({ ...formData, earnestMoneyValue: e.target.value })} placeholder="Earnest Money Amount" />
                        </div>
                        <div className="sm:col-span-3">
                          <label className="text-xs font-semibold text-gray-600">Payment Terms</label>
                          <textarea className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 resize-y min-h-[80px] outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white"
                            value={formData.paymentTerms || ''} onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })} placeholder="e.g. Net 30, 50% Advance" />
                        </div>
                      </div>
                    </div>
    
                    {/* Strategy & Notes */}
                    <div className="bg-emerald-50/30 p-6 rounded-xl border border-emerald-200 shadow-sm space-y-4">
                      <h3 className="text-xs font-bold text-emerald-700 uppercase tracking-widest flex items-center gap-1.5 border-b border-emerald-200 pb-3">
                        <Trophy className="w-4 h-4 shrink-0" /> Strategy & Notes
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Winning Strategy</label>
                          <textarea className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 resize-y min-h-[100px] outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 bg-white"
                            value={formData.winningStrategy || ''} onChange={(e) => setFormData({ ...formData, winningStrategy: e.target.value })} placeholder="Key strategy to win this proposal..." />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Notes</label>
                          <textarea className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 resize-y min-h-[100px] outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 bg-white"
                            value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." />
                        </div>
                      </div>
                    </div>
    </>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="bg-white w-full sm:max-w-5xl h-[95vh] sm:h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-start gap-3 sticky top-0 z-20">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                {profileObj?.projectName 
                  ? `${profileObj.projectName} - ${formData.title || formData.clientName || profileObj.clientName || (formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender')}` 
                  : (formData.title || formData.clientName || profileObj.clientName || (formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender'))}
              </h2>
              <span className={`px-2.5 py-0.5 text-[10px] sm:text-xs rounded-full border font-bold uppercase tracking-wider whitespace-nowrap ${
                (formData.status === 'Won' || formData.status === 'Awarded') ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                formData.status === 'Lost' ? 'bg-red-50 text-red-700 border-red-200' :
                formData.status === 'Hold' ? 'bg-gray-100 text-gray-700 border-gray-300' :
                'bg-slate-100 text-slate-700 border-slate-200'
              }`}>
                {formData.status}
              </span>
              {approvalStatus === 'APPROVED' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] sm:text-xs rounded-full border font-bold bg-emerald-50 text-emerald-700 border-emerald-200 whitespace-nowrap" title={formData.approvalRemarks || 'Approved'}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  Approved
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
              {tender.createdAt && (
                <span className="flex items-center gap-1 font-medium whitespace-nowrap">
                  <Clock className="w-3 h-3" />
                  <span className="text-gray-400 font-normal">Created</span>
                  {new Date(tender.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {tender.prequalification && (
                <span className="text-xs text-blue-600 font-medium">
                  From Prequalification #{tender.prequalification.id}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isCeo && onDelete && (
              <button onClick={() => setShowDeleteConfirm(true)} className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors" title="Delete Tender">
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Stage pills */}
        <div className="bg-slate-50 border-b border-gray-200 px-3 sm:px-6 py-2 flex gap-1.5 overflow-x-auto no-scrollbar">
          {AWARD_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => handleStageClick(s)}
              disabled={isLockedStatus}
              className={`px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-bold transition-all border whitespace-nowrap ${isLockedStatus
                ? 'opacity-40 cursor-not-allowed bg-white text-gray-400 border-gray-200'
                : formData.status === s
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm scale-105'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* ── Approval Workflow Banner ── */}
        {approvalStatus === 'PENDING_APPROVAL' && (
          <div className="mx-4 sm:mx-6 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs animate-fade-in">
            <div className="flex items-center gap-2.5 text-amber-900">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping shrink-0" />
              <div>
                <span className="font-bold">Pending Approval:</span> This {formData.type === 'PROPOSAL' ? 'proposal' : 'tender'} is awaiting review by CEO or GM Operations.
              </div>
            </div>
            {isApprover && (
              <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                <button type="button" onClick={() => setShowApproveModal(true)}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all">
                  <CheckCircle2 className="w-3.5 h-3.5" /><span>Approve</span>
                </button>
                <button type="button" onClick={() => setShowRejectModal(true)}
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all">
                  <XCircle className="w-3.5 h-3.5" /><span>Reject</span>
                </button>
              </div>
            )}
          </div>
        )}
        {approvalStatus === 'REJECTED' && (
          <div className="mx-4 sm:mx-6 mt-3 p-3 bg-rose-50 border border-rose-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs animate-fade-in">
            <div className="flex items-start gap-2.5 text-rose-900">
              <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Rejected / Revisions Requested</span>
                {formData.approvalRemarks && <p className="mt-0.5 text-rose-700 font-medium">"{formData.approvalRemarks}"</p>}
              </div>
            </div>
            <button type="button" onClick={async () => {
              try {
                setIsResubmitting(true);
                await approvalService.submit({ entityType: 'TENDER', entityId: tender.id, title: formData.title || `${formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender'} #${tender.id}` });
                const fresh = await approvalService.getByEntity('TENDER', tender.id);
                if (fresh) setApprovalRequest(fresh);
                setFormData(prev => ({ ...prev, approvalStatus: 'PENDING_APPROVAL', approvalRemarks: null }));
                setToast({ type: 'success', message: 'Resubmitted for approval!' });
                if (onSave) onSave({ ...tender, approvalStatus: 'PENDING_APPROVAL', approvalRemarks: null });
              } catch (err) {
                setToast({ type: 'error', message: err.message || 'Failed to resubmit' });
              } finally { setIsResubmitting(false); }
            }} disabled={isResubmitting}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all shrink-0 self-end sm:self-auto disabled:opacity-50">
              {isResubmitting ? 'Resubmitting...' : 'Resubmit for Approval'}
            </button>
          </div>
        )}

        {/* Stage Change Confirm */}
        <AnimatePresence>
        {stageChangeConfirm && stageChangeConfirm.to !== 'Won' && stageChangeConfirm.to !== 'Awarded' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-30 rounded-t-2xl sm:rounded-2xl p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl p-5 sm:p-6 w-full max-w-sm"
            >
              <h3 className="text-base font-bold text-gray-900 mb-1">Confirm Status Change</h3>
              <p className="text-sm text-gray-500 mb-4">
                Move tender from <span className="font-semibold text-gray-800"> {stageChangeConfirm.from} </span>
                to <span className="font-semibold text-blue-600"> {stageChangeConfirm.to}</span>?
              </p>
              {stageChangeConfirm.to === 'Lost' && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Why was this tender lost? <span className="text-red-500">*</span></label>
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {LOSS_REASONS.filter(r => ['price', 'competitor', 'other'].includes(r.id)).map((r) => (
                      <button key={r.id} type="button" onClick={() => setLossReason(r.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${lossReason === r.id ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes (Optional)</label>
                <textarea className="w-full p-2.5 border border-gray-200 rounded-xl text-sm h-20 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={stageChangeNote} onChange={(e) => setStageChangeNote(e.target.value)} placeholder="Add any notes..." />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={handleStageChangeCancel} disabled={isStageChanging} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors disabled:opacity-50">Cancel</button>
                <button onClick={handleStageChangeConfirm}
                  disabled={isStageChanging || (stageChangeConfirm.to === 'Lost' && !lossReason)}
                  className="px-5 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center">
                  {isStageChanging ? <Loader className="w-4 h-4 animate-spin" /> : 'Confirm'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        
        </AnimatePresence>

        <TenderWonConfirmModal
          isOpen={stageChangeConfirm && (stageChangeConfirm.to === 'Won' || stageChangeConfirm.to === 'Awarded')}
          onConfirm={handleStageChangeConfirm}
          onCancel={handleStageChangeCancel}
          isSaving={isStageChanging}
          canConfirm={
            !!formData.title &&
            !!formData.type &&
            !!formData.assignedTo &&
            !!formData.proposalValue &&
            !!formData.contractDuration &&
            !!formData.paymentTerms
          }
        >
          {renderFormFields()}
        </TenderWonConfirmModal>

        {/* Delete Confirm */}
        {showDeleteConfirm && (
          <DeleteConfirmModal
            title="Delete Tender"
            message="Are you sure you want to delete this tender? This action cannot be undone."
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={() => { setShowDeleteConfirm(false); onDelete(tender.id); }}
          />
        )}

        {/* Content */}
        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden bg-slate-50">
          {/* Sidebar tabs */}
          <div className="w-full sm:w-48 bg-white border-b sm:border-b-0 sm:border-r border-gray-200 p-2 sm:p-3 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-x-visible no-scrollbar shrink-0">
            <button onClick={() => setActiveTab('details')}
              className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${activeTab === 'details' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'}`}>
              <Edit3 className="w-4 h-4 shrink-0" /> Details
            </button>
            <button onClick={() => setActiveTab('activity')}
              className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${activeTab === 'activity' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'}`}>
              <Clock className="w-4 h-4 shrink-0" /> Timeline
              {timeline.length > 0 && (
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{timeline.length}</span>
              )}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-8">
            {activeTab === 'details' && (
              <div className="max-w-2xl mx-auto space-y-6">
                {renderFormFields()}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="flex flex-col h-full">
                {/* Timeline */}
                <div className="flex-1 space-y-4 mb-4 overflow-y-auto">
                  {timelineLoading ? (
                    [1, 2, 3].map(i => (
                      <div key={`sk-${i}`} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Skeleton variant="circular" width={20} height={20} animation="wave" />
                        </div>
                        <div className="flex-1 bg-white p-3 rounded-xl border shadow-sm">
                          <Skeleton width="60%" height={16} animation="wave" className="mb-2" />
                          <Skeleton width="40%" height={12} animation="wave" />
                        </div>
                      </div>
                    ))
                  ) : timeline.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{t('noActivityYet') || 'No activity yet'}</p>
                      <p className="text-xs mt-1">{t('addNoteToTrack') || 'Add a note to track progress'}</p>
                    </div>
                  ) : (
                    timeline.map((item, i) => {
                      const meta = getActivityMeta(item.type);
                      return (
                        <div key={i} className="flex gap-3">
                          <div className={`w-8 h-8 rounded-full bg-${meta.color}-100 flex items-center justify-center flex-shrink-0`}>
                            <span className="text-sm">{meta.icon}</span>
                          </div>
                          <div className="flex-1 bg-white p-3 rounded-xl border shadow-sm">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                {formatActivityMessage({ type: item.type, ...item.data })}
                              </p>
                              {item.isProfileHistory && (
                                <span className="inline-block bg-indigo-100 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 shrink-0">
                                  {t('profilePhase') || 'Profile Phase'}
                                </span>
                              )}
                              {item.isPrequalHistory && (
                                <span className="inline-block bg-blue-100 text-blue-700 text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 shrink-0">
                                  {t('prequalificationPhase') || 'Prequalification Phase'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              {item.user} • {item.timestamp.toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={activityEndRef} />
                </div>

                {/* Add Note Input */}
                <div className="flex gap-2 pt-4 border-t">
                  <input
                    className={`flex-1 p-3 rounded-xl border outline-none text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={t('addNotePlaceholder') || 'Add a note...'}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                    disabled={isLockedStatus}
                  />
                  <button
                    onClick={handleAddComment}
                    className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        {activeTab === 'details' && (
          <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-3 flex justify-end">
            <button onClick={handleSave} disabled={isSaving || isLockedStatus}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-60 shadow-sm shadow-blue-200">
              {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        )}

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        {/* ── Approval Modals ── */}
        {showApproveModal && (
          <ApproveRequestModal
            request={approvalRequest || {
              id: tender.id,
              entityType: 'TENDER',
              title: formData.title || `${formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender'} #${tender.id}`,
              requestedBy: tender.assignee || currentUser,
              createdAt: tender.createdAt
            }}
            users={users}
            onConfirm={handleApproveInModal}
            onClose={() => setShowApproveModal(false)}
          />
        )}
        {showRejectModal && (
          <RejectRequestModal
            request={approvalRequest || {
              id: tender.id,
              entityType: 'TENDER',
              title: formData.title || `${formData.type === 'PROPOSAL' ? 'Proposal' : 'Tender'} #${tender.id}`,
              requestedBy: tender.assignee || currentUser,
              createdAt: tender.createdAt
            }}
            onConfirm={handleRejectInModal}
            onClose={() => setShowRejectModal(false)}
          />
        )}
      </motion.div>
    </motion.div>
  );
}
